import React from "react";
import { useHistory } from "react-router-dom";
import { formatMoney } from "../../../util/currency";
import { types as sdkTypes } from "../../../util/sdkLoader";
import { useIntl } from "../../../util/reactIntl";
import { PrimaryButton, SecondaryButton, ResponsiveImage, AspectRatioWrapper, IconSpinner } from "../../../components";
import css from "../productsPage.module.css";

const { Money } = sdkTypes;

const ProductSections = (props) => {
    const { products, loading, currentPage, totalProducts, totalPages, onPageChange } = props;
    const history = useHistory();
    const intl = useIntl();

    const handleViewProduct = (product) => {
        const productTitle = product.title.replace(/ /g, '-').toLowerCase();
        // Navigate to product detail page and pass the product data through state
        history.push(`/l/${productTitle}/${product.id}`);
    };

    const handleBuyNow = (productURL) => {
        window.open(productURL, '_blank');
    };

    // Simple helper to format price with formatMoney
    const formatPrice = (price, currency) => {
        const moneyObject = new Money(price, currency);
        return formatMoney(intl, moneyObject);
    };

    // Helper function to truncate text
    const truncateText = (text, maxLength = 120) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    };

    // Use totalPages from props, fallback to calculation if not provided
    const itemsPerPage = 10;
    const calculatedTotalPages = Math.ceil(totalProducts / itemsPerPage);
    const finalTotalPages = totalPages || calculatedTotalPages;

    const renderPagination = () => {
        if (finalTotalPages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(finalTotalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Previous button
        items.push(
            <button
                key="prev"
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={css.paginationButton}
            >
                ‹
            </button>
        );

        // First page
        if (startPage > 1) {
            items.push(
                <button
                    key={1}
                    onClick={() => onPageChange(1)}
                    className={css.paginationButton}
                >
                    1
                </button>
            );
            if (startPage > 2) {
                items.push(<span key="ellipsis1" className={css.paginationEllipsis}>...</span>);
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <button
                    key={i}
                    className={`${css.paginationButton} ${i === currentPage ? css.paginationButtonActive : ''}`}
                    onClick={() => onPageChange(i)}
                >
                    {i}
                </button>
            );
        }

        // Last page
        if (endPage < finalTotalPages) {
            if (endPage < finalTotalPages - 1) {
                items.push(<span key="ellipsis2" className={css.paginationEllipsis}>...</span>);
            }
            items.push(
                <button
                    key={finalTotalPages}
                    onClick={() => onPageChange(finalTotalPages)}
                    className={css.paginationButton}
                >
                    {finalTotalPages}
                </button>
            );
        }

        // Next button
        items.push(
            <button
                key="next"
                onClick={() => onPageChange(Math.min(finalTotalPages, currentPage + 1))}
                disabled={currentPage === finalTotalPages}
                className={css.paginationButton}
            >
                ›
            </button>
        );

        return (
            <div className={css.paginationContainer}>
                {items}
            </div>
        );
    };

    if (loading) {
        return (
            <div className={css.loadingContainer}>
                <IconSpinner />
                <p>Loading products...</p>
            </div>
        );
    }

    if (!products || products.length === 0) {
        return (
            <div className={css.noProductsContainer}>
                <h3>No products found</h3>
                <p>Try adjusting your search criteria or filters.</p>
            </div>
        );
    }

    return (    
        <div className={css.productsSection}>
            <div className={css.productGrid}>
                {products.map((product) => {
                    // Create image object for ResponsiveImage
                    const productImage = {
                        id: { uuid: `img-${product.id}` },
                        type: 'image',
                        attributes: {
                            variants: {
                                'listing-card': { width: 400, height: 300, url: product.thumbnailImage },
                                'listing-card-2x': { width: 800, height: 600, url: product.thumbnailImage },
                                'listing-card-4x': { width: 1200, height: 900, url: product.thumbnailImage }
                            }
                        }
                    };

                    return (
                        <div key={product.id} className={css.productCard}>
                            <AspectRatioWrapper width={4} height={3} className={css.imageWrapper}>
                                <ResponsiveImage
                                    rootClassName={css.productImage}
                                    image={productImage}
                                    alt={product.title}
                                    variants={['listing-card', 'listing-card-2x', 'listing-card-4x']}
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                />
                            </AspectRatioWrapper>
                            <div className={css.cardBody}>
                                <h3 className={css.cardTitle}>{product.title}</h3>
                                <p className={css.cardPrice}>{formatPrice(product.price, product.currency)}</p>
                                <p className={css.cardDescription} title={product.description}>
                                    {truncateText(product.description, 120)}
                                </p>
                                <div className={css.cardActions}>
                                    <PrimaryButton 
                                        onClick={() => handleBuyNow(product.productURL)}
                                        className={css.buyNowButton}
                                    >
                                        Buy Now
                                    </PrimaryButton>
                                    <SecondaryButton 
                                        onClick={() => handleViewProduct(product)} 
                                        className={css.viewProductButton}
                                    >
                                        View Product
                                    </SecondaryButton>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {renderPagination()}
        </div>
    );
};

export default ProductSections;